import { createRouter, createWebHistory } from "vue-router";
import { routeState } from "@/utils/routeState.js";
import {
  loginGuards,
  studentGuards,
  adminGuards,
  evaluatorGuards,
  proctorGuards,
  configGuards,
  examinerGuards,
  subAdminGuards,
  examCoordinatorGuards,
  examManagementGuards,
  examViewGuards,
  questionBankViewGuards,
} from "./guards";

const routes = [
  {
    path: "/:catchAll(.*)",
    name: "Error404",
    component: () => import("@/views/CommonPages/PageNotFound.vue"),
    meta: { title: "Page Not Found | ExamStack" },
  },
  {
    path: "/",
    name: "Login",
    component: () => import("../views/Authentication/LoginView.vue"),
    beforeEnter: loginGuards,
    meta: { title: "Login | ExamStack" },
  },
  {
    path: "/sso-login",
    name: "SsoLogin",
    component: () => import("../views/Authentication/SsoLoginView.vue"),
    meta: { title: "SSO Login | ExamStack" },
  },
  // Admin dashboard
  {
    path: "/admin/dashboard",
    name: "AdminDashboardView",
    component: () => import("../views/AdminDashboard/DashboardViewV4.vue"),
    beforeEnter: subAdminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // {
  //   path: "/admin/dashboard2",
  //   name: "AdminDashboardView2",
  //   component: () => import("../views/AdminDashboard/DashboardViewV4.vue"),
  //   beforeEnter: subAdminGuards,
  //   meta: { title: "Admin | ExamStack" },
  // },
  // Exam Coordinator dashboard
  {
    path: "/exam-coordinator/dashboard",
    name: "ExamCoordinatorDashboard",
    component: () => import("../views/ExamCoordinatorViews/DashboardView.vue"),
    beforeEnter: examCoordinatorGuards,
    meta: { title: "Exam Coordinator | ExamStack" },
  },
  {
    path: "/admin-exam",
    name: "ExamView",
    component: () => import("../views/AdminDashboard/Exam/ExamView.vue"),
    beforeEnter: examViewGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exams" },
      ],
    },
  },
  {
    path: "/admin-question-bank",
    name: "QuestionView",
    component: () =>
      import("../views/AdminDashboard/QuestionBank/QuestionView.vue"),
    beforeEnter: questionBankViewGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Question Bank" },
      ],
    },
  },
  {
    path: "/exam-builder/:id/:questionId?",
    name: "ExamBuilder",
    component: () =>
      import("@/views/AdminDashboard/QuestionBank/ExamBuilder.vue"),
    beforeEnter: examManagementGuards,
    props: true,
    meta: {
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Question Bank", to: { name: "QuestionView" } },
        { label: "Exam Builder" },
      ],
    },
  },
  {
    path: "/question-create/:id?",
    name: "GenerateQuestions",
    component: () =>
      import("@/views/AdminDashboard/QuestionBank/GenerateQuestions.vue"),
    beforeEnter: examManagementGuards,
    props: true,
    meta: {
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Question Bank", to: { name: "QuestionView" } },
        { label: "Generate Questions" },
      ],
    },
  },
  {
    path: "/student-list",
    name: "StudentList",
    component: () =>
      import("../views/AdminDashboard/StudentDetails/StudentList.vue"),
    beforeEnter: subAdminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Students" },
      ],
    },
  },
  {
    path: "/user-list",
    name: "UserList",
    component: () => import("../views/AdminDashboard/UserList.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Users" },
      ],
    },
  },
  {
    path: "/admin/kyc-list",
    name: "KycList",
    component: () => import("../views/AdminDashboard/KycList.vue"),
    beforeEnter: examViewGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "KYC" },
      ],
    },
  },
  {
    path: "/question-review/:id?",
    name: "QuestionReview",
    component: () => import("../views/AdminDashboard/QuestionReview.vue"),
    beforeEnter: questionBankViewGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Question Bank", to: { name: "QuestionView" } },
        { label: "Question Review" },
      ],
    },
  },
  // exam configurations
  {
    path: "/exam-create/:id?",
    name: "ExamCreate",
    component: () => import("../views/AdminDashboard/Exam/ExamCreate.vue"),
    beforeEnter: examManagementGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exams", to: { name: "ExamView" } },
        { label: "Create Exam" },
      ],
    },
  },
  {
    path: "/exam-configurations/:id?",
    name: "ExamConfiguration",
    component: () =>
      import("../views/AdminDashboard/Exam/ExamConfiguration.vue"),
    beforeEnter: examManagementGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exams", to: { name: "ExamView" } },
        { label: "Exam Configuration" },
      ],
    },
  },
  {
    path: "/admin/exam/preview/:id?",
    name: "ExamDetails",
    component: () => import("../views/AdminDashboard/Exam/ExamDetails.vue"),
    beforeEnter: examViewGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exams", to: { name: "ExamView" } },
        { label: "Exam Details" },
      ],
    },
  },
  {
    path: "/admin/exam/enrolled-candidates/:id?",
    name: "ExamEnrolledCandidates",
    component: () =>
      import("../views/AdminDashboard/Exam/EnrolledCandidatesView.vue"),
    beforeEnter: examManagementGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exams", to: { name: "ExamView" } },
        { label: "Enrolled Candidates" },
      ],
    },
  },
  {
    path: "/admin/live-kit",
    name: "LiveKitDashboard",
    component: () => import("../views/AdminDashboard/LiveKitDashboard.vue"),
    beforeEnter: subAdminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin/live-kit-home",
    name: "LiveKitDashboardHome",
    component: () => import("../views/AdminDashboard/ExamListLiveKit.vue"),
    beforeEnter: subAdminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin/live-kit-rooms",
    name: "LiveKitRoomList",
    component: () => import("../views/AdminDashboard/LiveKitRoomList.vue"),
    beforeEnter: subAdminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin/live-kit-active-rooms",
    name: "LiveKitActiveRooms",
    component: () => import("../views/AdminDashboard/LiveKitActiveRooms.vue"),
    beforeEnter: subAdminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // admin reports
  {
    path: "/reports",
    name: "ExamReportsView",
    component: () => import("../views/AdminDashboard/ExamReportsView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/reports/exam",
    name: "AdminExamReports",
    component: () =>
      import("../views/AdminDashboard/Reports/AdminExamReports.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exam Reports" },
      ],
    },
  },
  {
    path: "/reports/downloads",
    name: "AdminDownloadsList",
    component: () =>
      import("../views/AdminDashboard/Reports/AdminDownloadsList.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "All Downloads" },
      ],
    },
  },
  {
    path: "/reports/exam/student/:id?",
    name: "ExamStudentList",
    component: () =>
      import("../views/AdminDashboard/Reports/ExamStudentList.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exam Reports", to: { name: "AdminExamReports" } },
        { label: "Student List" },
      ],
    },
  },
  {
    path: "/reports/question-bank-analysis",
    name: "ReportsQuestionBank",
    component: () =>
      import("../views/AdminDashboard/Reports/QuestionBankAnalysis.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/reports/question-bank",
    name: "QuestionBankReport",
    component: () =>
      import("../views/AdminDashboard/Reports/QuestionBankReport.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/reports/student-logs/:exam_id/:id?",
    name: "StudentLogs",
    component: () => import("../views/AdminDashboard/Reports/StudentLogs.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exam Reports", to: { name: "AdminExamReports" } },
        { label: "Student Logs" },
      ],
    },
  },
  {
    path: "/reports/exam/student-answer/:exam_id/:id?",
    name: "EvaluateStudentAnswer",
    component: () =>
      import("../views/AdminDashboard/Reports/EvaluateStudentAnswerView.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Exam Reports", to: { name: "AdminExamReports" } },
        { label: "Evaluate Answer Sheet" },
      ],
    },
  },

  // admin results
  {
    path: "/results/result-list",
    name: "AdminResults",
    component: () => import("../views/AdminDashboard/Result/AdminResults.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Results" },
      ],
    },
  },
  {
    path: "/results/result-approve",
    name: "ResultApproval",
    component: () =>
      import("../views/AdminDashboard/Result/ResultApproval.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // exam results (shared: Admin + Examiner)
  {
    path: "/results/exams",
    name: "ResultsExamList",
    component: () =>
      import("../views/CommonPages/Result/ResultExamListView.vue"),
    beforeEnter: examinerGuards,
    meta: { title: "Exam Results | ExamStack" },
  },
  {
    path: "/results/exams/:id/students",
    name: "ResultsStudentList",
    component: () =>
      import("../views/CommonPages/Result/ResultStudentListView.vue"),
    beforeEnter: examinerGuards,
    meta: { title: "Student Results | ExamStack" },
  },
  {
    path: "/results/exams/:examId/students/:studentId/answer-sheet",
    name: "ResultsStudentAnswer",
    component: () =>
      import("../views/CommonPages/Result/ResultStudentAnswerView.vue"),
    beforeEnter: examinerGuards,
    meta: { title: "Student Answer Sheet | ExamStack" },
  },
  // admin student details page
  {
    path: "/admin/student-detail/:id?",
    name: "StudentDetails",
    component: () =>
      import("..//views/AdminDashboard/StudentDetails/StudentDetails.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Students", to: { name: "StudentList" } },
        { label: "Student Details" },
      ],
    },
  },
  // email template
  {
    path: "/email/template-list",
    name: "EmailList",
    component: () =>
      import("../views/AdminDashboard/EmailTemplate/EmailList.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Email Templates" },
      ],
    },
  },
  {
    path: "/email/template-form/:id?",
    name: "CreateEmailTemplate",
    component: () =>
      import("../views/AdminDashboard/EmailTemplate/CreateEmailTemplate.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Email Templates", to: { name: "EmailList" } },
        { label: "Edit Template" },
      ],
    },
  },
  {
    path: "/email/template-create/:id?",
    name: "EmailTemplate",
    component: () =>
      import("../views/AdminDashboard/EmailTemplate/EmailTemplate.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Email Templates", to: { name: "EmailList" } },
        { label: "Create Template" },
      ],
    },
  },
  // settings
  {
    path: "/settings/",
    name: "SettingsView",
    component: () =>
      import("../views/AdminDashboard/Settings/SettingsView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/settings/slots/",
    name: "SlotsView",
    component: () => import("../views/AdminDashboard/Settings/SlotsView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // Evaluator routes
  {
    path: "/evaluator/list/",
    name: "AdminEvaluatorList",
    component: () =>
      import("../views/AdminDashboard/Evaluator/EvaluatorList.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Evaluators" },
      ],
    },
  },
  // Daily exam limit override routes
  {
    path: "/daily-exam-limits/",
    name: "AdminDailyExamLimitList",
    component: () =>
      import("../views/AdminDashboard/DailyExamLimit/DailyExamLimitList.vue"),
    beforeEnter: adminGuards,
    meta: {
      title: "Admin | ExamStack",
      breadcrumb: [
        { label: "Admin Dashboard", to: { name: "AdminDashboardView" } },
        { label: "Daily Exam Limits" },
      ],
    },
  },
  // Proctor routes
  {
    path: "/proctor/dashboard",
    name: "ProctorDashboard",
    component: () => import("../views/ProctorDashboard/DashboardView.vue"),
    beforeEnter: proctorGuards,
    meta: { title: "Proctor | ExamStack" },
  },
  {
    path: "/proctor/exam-list",
    name: "ProctorExamList",
    component: () => import("../views/ProctorDashboard/ExamList.vue"),
    beforeEnter: proctorGuards,
    meta: { title: "Proctor | ExamStack" },
  },
  // Student Routes
  {
    path: "/examine",
    name: "StudentDashboardView",
    component: () => import("@/views/StudentViews/DashboardView.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Student Dashboard | ExamStack" },
  },
  {
    path: "/examine/exam-acknowledgement",
    name: "StudentExamEnvironment",
    component: () => import("@/views/StudentViews/ExamAcknowledgement.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam Acknowledgement | ExamStack" },
  },
  {
    path: "/examine/system-check",
    name: "StudentSystemCheck",
    component: () => import("@/views/StudentViews/SystemCheck.vue"),
    beforeEnter: studentGuards,
    meta: { title: "System Check | ExamStack" },
  },
  {
    path: "/exam",
    name: "StudentExam",
    component: () => import("@/views/StudentViews/ExamLoader.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam | ExamStack" },
  },
  {
    path: "/exam-interface",
    name: "StudentExamInterface",
    component: () => import("@/views/StudentViews/ExamView.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam Interface | ExamStack" },
  },
  {
    path: "/exam-feedback",
    name: "StudentExamFeedback",
    component: () => import("@/views/StudentViews/FeedbackForm.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Feedback | ExamStack" },
  },
  {
    path: "/examine/exam-list",
    name: "StudentExamList",
    component: () => import("@/views/StudentViews/ExamList/ExamList.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam List | ExamStack" },
  },
  {
    path: "/examine/exam-result",
    name: "StudentExamResults",
    component: () => import("@/views/StudentViews/ExamList/ExamResult.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Result | ExamStack" },
  },
  {
    path: "/examine/exam-certificates",
    name: "StudentExamCertificates",
    component: () =>
      import("@/views/StudentViews/ExamList/ExamCertificates.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Certificates | ExamStack" },
  },
  {
    path: "/examine/profile",
    name: "StudentProfile",
    component: () => import("@/views/StudentViews/StudentProfile.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Profile | ExamStack" },
  },
  {
    path: "/student/tickets",
    name: "TicketsList",
    component: () => import("@/views/StudentViews/Help/TicketsList.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Tickets | ExamStack" },
  },
  {
    path: "/mobile-upload",
    name: "MobileUploadView",
    component: () => import("@/views/StudentViews/MobileUploadView.vue"),
    meta: { title: "Mobile Upload | ExamStack" },
  },
  // common pages
  {
    path: "/profile",
    name: "ProfileView",
    component: () => import("../views/CommonPages/ProfileView.vue"),
    meta: { title: "Profile | ExamStack" },
  },
  {
    path: "/forget-password",
    name: "ForgetPassword",
    component: () => import("../views/CommonPages/ForgetPassword.vue"),
    meta: { title: "FP | ExamStack" },
  },
  {
    path: "/reset-password/:id?",
    name: "ResetPassword",
    component: () => import("../views/CommonPages/ResetPassword.vue"),
    meta: { title: "FP | ExamStack" },
  },
  {
    path: "/notifications",
    name: "NotificationView",
    component: () => import("../views/CommonPages/NotificationView.vue"),
    meta: { title: "Notifications | ExamStack" },
  },
  {
    path: "/configuration/batch",
    name: "BatchList",
    component: () => import("@/views/CommonPages/Configurations/BatchList.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/batch-create",
    name: "BatchForm",
    component: () =>
      import("@/views/CommonPages/Configuration/BatchFormV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/program",
    name: "ProgramList",
    component: () =>
      import("@/views/CommonPages/Configurations/ProgramList.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/program-create",
    name: "ProgramForm",
    component: () =>
      import("@/views/CommonPages/Configuration/ProgramFormV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/elective",
    name: "ElectiveList",
    component: () =>
      import("@/views/CommonPages/Configurations/ElectiveList.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/elective-create",
    name: "ElectiveForm",
    component: () =>
      import("@/views/CommonPages/Configuration/ElectiveFormV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/batch-elective-map",
    name: "AcademicBatchSpecialization",
    component: () =>
      import(
        "@/views/CommonPages/Configuration/AcademicBatchSpecializationV2.vue"
      ),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/semester",
    name: "SemesterList",
    component: () =>
      import("@/views/CommonPages/Configurations/SemesterList.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/semester-create",
    name: "SemesterForm",
    component: () =>
      import("@/views/CommonPages/Configuration/SemesterFormV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/course",
    name: "CourseList",
    component: () =>
      import("@/views/CommonPages/Configurations/CourseList.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  // course create
  {
    path: "/configuration/course-create",
    name: "CourseForm",
    component: () =>
      import("@/views/CommonPages/Configuration/CourseFormV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/course-mapping",
    name: "CourseProgramMap",
    component: () =>
      import("@/views/CommonPages/Configuration/CourseProgramMapV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  {
    path: "/configuration/course-matrix",
    name: "CourseMatrix",
    component: () =>
      import("@/views/CommonPages/Configuration/CourseMatrixV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  // course po create
  {
    path: "/configuration/program-po",
    name: "ProgramPOs",
    component: () =>
      import("@/views/CommonPages/Configuration/ProgramPOsV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  // course pos and co map
  {
    path: "/configuration/course/po/co",
    name: "CoursePoCoMap",
    component: () =>
      import("@/views/CommonPages/Configuration/CoursePoCoMapV2.vue"),
    meta: { title: "configuration | ExamStack" },
    beforeEnter: configGuards,
  },
  // Evaluator dashboard
  {
    path: "/evaluator/dashboard",
    name: "EvaluatorDashboard",
    component: () => import("../views/EvaluatorV1/EvaluatorDashboard.vue"),
    beforeEnter: evaluatorGuards,
    meta: { title: "Evaluator | ExamStack" },
  },
  {
    path: "/evaluator/v1/evaluate/:id?",
    name: "EvaluatorV1DescriptiveEvaluation",
    component: () =>
      import("../views/EvaluatorV1/DescriptiveEvaluationView.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Evaluate | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Evaluate Scripts" },
      ],
    },
  },
  {
    path: "/evaluator/v1/assigned-exams",
    name: "EvaluatorV1AssignedExams",
    component: () => import("../views/EvaluatorV1/AssignedExamsView.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Allocated Electives | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Allocated Electives" },
      ],
    },
  },
  {
    path: "/evaluator/exam",
    name: "EvaluatorExam",
    component: () => import("../views/EvaluatorViews/EvaluatorExam.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Evaluator | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Exams" },
      ],
    },
  },
  {
    path: "/evaluator/exam/:id",
    name: "EvaluatorExamDetails",
    component: () => import("../views/EvaluatorViews/EvaluatorExamDetails.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Evaluator | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Exams", to: { name: "EvaluatorExam" } },
        { label: "Exam Details" },
      ],
    },
  },
  {
    path: "/evaluator/exam/evaluation/:id/:user_id?",
    name: "EvaluatorEvaluation",
    component: () => import("../views/EvaluatorViews/EvaluatorEvaluation.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Evaluator | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Exams", to: { name: "EvaluatorExam" } },
        { label: "Evaluation" },
      ],
    },
  },
  {
    path: "/evaluator/exam/evaluation-descriptive/:id/:user_id?",
    name: "EvaluatorDescriptiveEvaluation",
    component: () =>
      import("../views/EvaluatorViews/EvaluatorDescriptiveEvaluation.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Evaluator | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Exams", to: { name: "EvaluatorExam" } },
        { label: "Descriptive Evaluation" },
      ],
    },
  },
  {
    path: "/evaluator/exam/review/:id/:user_id?",
    name: "EvaluationWorkspace",
    component: () => import("../views/EvaluatorViews/EvaluationWorkspace.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Evaluator | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Exams", to: { name: "EvaluatorExam" } },
        { label: "Review Workspace" },
      ],
    },
  },
  {
    path: "/evaluator/assigned-students/:id?",
    name: "AssignedStudents",
    component: () => import("../views/EvaluatorViews/AssignedStudents.vue"),
    beforeEnter: evaluatorGuards,
    meta: {
      title: "Evaluator | ExamStack",
      breadcrumb: [
        { label: "Evaluator Dashboard", to: { name: "EvaluatorDashboard" } },
        { label: "Exams", to: { name: "EvaluatorExam" } },
        { label: "Assigned Students" },
      ],
    },
  },
  // service pages
  {
    path: "/maintenance",
    name: "MaintenanceView",
    component: () => import("../views/CommonPages/MaintenanceView.vue"),
    meta: { title: "Maintenance | ExamStack" },
  },
  // network error page
  {
    path: "/network-disconnect",
    name: "NetworkDisconnect",
    component: () => import("../views/CommonPages/NetworkDisconnect.vue"),
    meta: { title: "Network | ExamStack" },
  },
  // access denied page
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: () => import("../views/CommonPages/AccessDenied.vue"),
    meta: { title: "Network | ExamStack" },
  },
  // examiner routes
  {
    path: "/examiner/dashboard",
    name: "ExaminerDashboard",
    component: () => import("../views/ExaminerViews/ExaminerDashboard.vue"),
    beforeEnter: examinerGuards,
    meta: { title: "Examiner Dashboard | ExamStack" },
  },
  {
    path: "/examiner/exam-list",
    name: "ExaminerExamTable",
    component: () => import("../views/ExaminerViews/ExaminerExamTable.vue"),
    beforeEnter: examinerGuards,
    meta: {
      title: "Examiner Exam List | ExamStack",
      breadcrumb: [
        { label: "Examiner Dashboard", to: { name: "ExaminerDashboard" } },
        { label: "Exams" },
      ],
    },
  },
  {
    path: "/examiner/evaluator-list",
    name: "ExaminerEvaluatorTable",
    component: () =>
      import("../views/ExaminerViews/ExaminerEvaluatorTable.vue"),
    beforeEnter: examinerGuards,
    meta: {
      title: "Examiner Evaluator List | ExamStack",
      breadcrumb: [
        { label: "Examiner Dashboard", to: { name: "ExaminerDashboard" } },
        { label: "Evaluators" },
      ],
    },
  },
  {
    path: "/examiner/due-date-list",
    name: "ExaminerDueDateTable",
    component: () => import("../views/ExaminerViews/ExaminerDueDateTable.vue"),
    beforeEnter: examinerGuards,
    meta: {
      title: "Examiner Due-Date List | ExamStack",
      breadcrumb: [
        { label: "Examiner Dashboard", to: { name: "ExaminerDashboard" } },
        { label: "Due Dates" },
      ],
    },
  },
  {
    path: "/examiner/unassigned-reports/:id?",
    name: "ExaminerUnassignedReports",
    component: () => import("../views/ExaminerViews/UnassignedReports.vue"),
    beforeEnter: examinerGuards,
    meta: {
      title: "Unassigned Reports | ExamStack",
      breadcrumb: [
        { label: "Examiner Dashboard", to: { name: "ExaminerDashboard" } },
        { label: "Exams", to: { name: "ExaminerExamTable" } },
        { label: "Unassigned Reports" },
      ],
    },
  },
  {
    path: "/examiner/exam-panels",
    name: "EvaluatorPanelExamList",
    component: () =>
      import("../views/ExaminerViews/EvaluatorPanelExamList.vue"),
    beforeEnter: examinerGuards,
    meta: {
      title: "Examiner Panels | ExamStack",
      breadcrumb: [
        { label: "Examiner Dashboard", to: { name: "ExaminerDashboard" } },
        { label: "Examiner Panels" },
      ],
    },
  },
  {
    path: "/examiner/evaluator-create",
    name: "ExaminerEvaluatorCreate",
    component: () => import("../views/ExaminerViews/EvaluatorCreate.vue"),
    beforeEnter: examinerGuards,
    meta: {
      title: "Create Evaluator | ExamStack",
      breadcrumb: [
        { label: "Examiner Dashboard", to: { name: "ExaminerDashboard" } },
        { label: "Create Evaluator" },
      ],
    },
  },
  // student calculator
  {
    path: "/calculator",
    name: "ScientificCalculator",
    component: () => import("../views/CommonPages/ScientificCalculator.vue"),
    meta: { title: "Scientific Calculator | ExamStack" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return { top: 0, behavior: "smooth" };
  },
});

router.afterEach((to) => {
  const defaultTitle = "ExamStack";

  if (to.meta && to.meta.title) {
    document.title = to.meta.title;
  } else {
    document.title = defaultTitle;
  }
});

router.beforeEach((to, from, next) => {
  const isOffline = !navigator.onLine;

  if (!isOffline && to.name !== "NetworkDisconnect") {
    routeState.lastRoute = to.fullPath;
  }

  next();
});

router.onError((error, to) => {
  const errors = [
    "Failed to fetch dynamically imported module",
    "Importing a module script failed",
  ];
  if (
    error.name === "ChunkLoadError" ||
    errors.some((e) => error.message.includes(e))
  ) {
    if (to?.fullPath) {
      window.location.href = to.fullPath;
    } else {
      window.location.reload();
    }
  }
});

// router.beforeEach((to, from, next) => {
//   const isOffline = !window.navigator.onLine;

//   if (!isOffline && to.name !== "NetworkDisconnect") {
//     localStorage.setItem("last_visited_page", to.fullPath);
//   }

//   if (isOffline) {
//     if (to.name !== "NetworkDisconnect") {
//       return next({ name: "NetworkDisconnect" });
//     }
//     return next(); // already on disconnect page
//   }

//   if (!isOffline && to.name === "NetworkDisconnect") {
//     const lastPage = localStorage.getItem("last_visited_page");

//     if (lastPage) {
//       localStorage.removeItem("last_visited_page");
//       return next(lastPage);
//     }

//     return next("/");
//   }

//   next();
// });
// router.beforeEach((to, from, next) => {
//   const user = JSON.parse(localStorage.getItem("user"));

//   if (to.meta.roles && !to.meta.roles.includes(user?.role)) {
//     return next({
//       name: "AccessDenied",
//       params: { resourceName: to.name },
//     });
//   }

//   next();
// });

// router.beforeEach((to, from, next) => {
//   if (to.name !== "NetworkDisconnect") {
//     localStorage.setItem("last_visited_page", to.fullPath);
//   }

//   if (!window.navigator.onLine && to.name !== "NetworkDisconnect") {
//     next({ name: "NetworkDisconnect" });
//   } else {
//     next();
//   }
// });

export default router;
